import {
  FaMapMarkerAlt,
  FaRegClock,
  FaStar,
  FaPlane,
  FaMountain,
  FaUtensils
} from "react-icons/fa";
import { IoMdArrowDropright } from "react-icons/io";
import { AppContext } from "../../App";
import { useContext } from "react";
import imageHero from "../../assets/kili2.jpg";
import imageDay1 from "../../assets/kilimanjaro.jpg";
import imageDay2 from "../../assets/kili2.jpg";
import imageDay3 from "../../assets/beans.jpg";

const hero = {
  kicker: "Rongai Route",
  title: "Northern Serenade",
  subtitle: "Remote northern approach with sweeping views and calm trails.",
  backgroundImage: imageHero,
  meta: ["Quiet route", "Wide vistas", "Strong acclimatization"]
};

const overview = `Rongai Route on Mount Kilimanjaro is a preferred choice for those seeking a more serene trek. Taking six to seven days, the route guides trekkers through lush rainforests, heathlands, and alpine deserts, showcasing a diverse range of landscapes. Its unique northern approach to the summit provides a tranquil and less crowded experience.`;

/* ---------------- HIGHLIGHTS ---------------- */

const highlights = [
  {
    icon: FaMapMarkerAlt,
    title: "Northern trail",
    description: "Enter from Rongai Gate through fragrant forests and remote vistas.",
    color: "#8b5cf6"
  },
  {
    icon: FaStar,
    title: "Kibo ascent",
    description: "Climb through Kikelewa and Mawenzi camps before the final summit push.",
    color: "#06b6d4"
  },
  {
    icon: FaPlane,
    title: "Sunrise summit",
    description: "Reach Uhuru Peak at dawn with spectacular views across East Africa.",
    color: "#f59e0b"
  },
  {
    icon: FaRegClock,
    title: "Best seasons",
    description: "January–March and June–October provide the most stable weather.",
    color: "#16a34a"
  }
];

/* ---------------- DETAILS ---------------- */

const details = [
  {
    icon: FaMountain,
    title: "Route",
    description: "Rongai Gate → Simba → Kikelewa → Mawenzi → Kibo → Uhuru Peak"
  },
  {
    icon: FaRegClock,
    title: "Duration",
    description: "6–7 day trek with steady acclimatization"
  },
  {
    icon: FaUtensils,
    title: "Meals",
    description: "Full board mountain meals prepared by expedition chefs"
  }
];

/* ---------------- ITINERARY ---------------- */

const itinerary = [
  {
    dayNum: "01",
    title: "Rongai Gate to Simba Camp (2,650m)",
    image: imageDay1,
    body: "Commence the trek at Rongai Gate, immersing in lush rainforest before reaching Simba Camp. The day introduces the route gently while supporting early acclimatization.",
    activities: "Accommodation: Simba Camp",
    tags: ["Forest trek", "Acclimatization"]
  },
  {
    dayNum: "02",
    title: "Simba Camp to Kikelewa Camp (3,600m)",
    image: imageDay2,
    body: "Ascend through moorland terrain with panoramic Kilimanjaro views. Kikelewa Camp provides a peaceful setting for rest and acclimatization.",
    activities: "Accommodation: Kikelewa Camp",
    tags: ["Moorland", "Panoramic views"]
  },
  {
    dayNum: "03",
    title: "Kikelewa Camp to Mawenzi Tarn Camp (4,330m)",
    image: imageDay3,
    body: "Traverse alpine desert landscapes toward Mawenzi Peak. Mawenzi Tarn Camp sits beside a glacial lake offering dramatic scenery.",
    activities: "Accommodation: Mawenzi Tarn Camp",
    tags: ["Alpine desert", "Acclimatization"]
  }
];

export default function RongaiRoute() {

  const appContext = useContext(AppContext);

  const theme = {
    primary: "#7c3aed",
    primaryLight: "#8b5cf6",
    accent: "#4c1d95",
    bg: "#faf5ff",
    cardBg: "#ffffff",
    text: "#1e293b",
    textMuted: "#64748b"
  };

  return (
    <div
      style={{
        maxWidth: "1400px",
        margin: "0 auto",
        fontFamily: "system-ui, sans-serif",
        background: theme.bg,
        color: theme.text
      }}
    >

      {/* HERO */}

      <section
        style={{
          height: "75vh",
          backgroundImage: `linear-gradient(135deg, rgba(20,15,35,0.9), rgba(90,40,150,0.75)), url(${hero.backgroundImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          color: "white",
          padding: "0 2rem"
        }}
      >
        <div style={{ maxWidth: 800 }}>

          <span
            style={{
              background: "rgba(139,92,246,0.25)",
              border: "1px solid rgba(139,92,246,0.45)",
              padding: "6px 20px",
              borderRadius: "40px",
              fontSize: "0.9rem",
              fontWeight: 600
            }}
          >
            {hero.kicker}
          </span>

          <h1 style={{ fontSize: "clamp(2.5rem,6vw,4rem)", marginTop: "1rem" }}>
            {hero.title}
          </h1>

          <p style={{ fontSize: "1.3rem", marginBottom: "2rem" }}>
            {hero.subtitle}
          </p>

          <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
            {hero.meta.map((m,i)=>(
              <span
                key={i}
                style={{
                  background: "rgba(255,255,255,0.18)",
                  padding: "6px 14px",
                  borderRadius: "40px",
                  fontSize: "0.85rem"
                }}
              >
                {m}
              </span>
            ))}
          </div>

        </div>
      </section>

      <div style={{ padding:"4rem 1.5rem" }}>

        {/* OVERVIEW */}

        <section style={{ textAlign:"center", maxWidth:800, margin:"0 auto 5rem" }}>
          <h2 style={{ fontSize:"2.2rem", marginBottom:"1rem" }}>
            Rongai Route Overview
          </h2>

          <p style={{ lineHeight:1.8, color:theme.textMuted }}>
            {overview}
          </p>

          {/* QUICK DETAILS */}

          <div
            style={{
              display:"flex",
              gap:"1rem",
              marginTop:"2rem",
              flexWrap:"wrap",
              justifyContent:"center"
            }}
          >
            {details.map((d,i)=>{
              const Icon = d.icon;

              return(
                <div
                  key={i}
                  style={{
                    background:"white",
                    padding:"1rem 1.5rem",
                    borderRadius:"12px",
                    display:"flex",
                    gap:10,
                    alignItems:"center",
                    boxShadow:"0 4px 12px rgba(0,0,0,0.05)"
                  }}
                >
                  <Icon size={20} style={{color:theme.primary}} />

                  <div>
                    <div style={{fontWeight:600}}>{d.title}</div>
                    <div style={{fontSize:"0.85rem", color:theme.textMuted}}>
                      {d.description}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </section>

        {/* HIGHLIGHTS */}

        <section style={{marginBottom:"5rem"}}>
          <h2 style={{textAlign:"center", marginBottom:"2rem"}}>
            Route Highlights
          </h2>

          <div
            style={{
              display:"grid",
              gridTemplateColumns:"repeat(auto-fit,minmax(260px,1fr))",
              gap:"1.5rem"
            }}
          >
            {highlights.map((h,i)=>{
              const Icon = h.icon;

              return(
                <article
                  key={i}
                  style={{
                    background:"white",
                    padding:"1.6rem",
                    borderRadius:"14px",
                    boxShadow:"0 6px 16px rgba(0,0,0,0.06)"
                  }}
                >
                  <div
                    style={{
                      width:50,
                      height:50,
                      borderRadius:10,
                      background:`${h.color}15`,
                      display:"flex",
                      alignItems:"center",
                      justifyContent:"center",
                      color:h.color,
                      fontSize:"1.4rem",
                      marginBottom:"1rem"
                    }}
                  >
                    <Icon/>
                  </div>

                  <h3 style={{fontSize:"1.2rem"}}>{h.title}</h3>
                  <p style={{color:theme.textMuted}}>{h.description}</p>
                </article>
              )
            })}
          </div>
        </section>

        {/* ITINERARY */}

        <section>
          <h2 style={{textAlign:"center", marginBottom:"3rem"}}>
            Trekking Itinerary
          </h2>

          {itinerary.map((day,i)=>(
            <article
              key={i}
              style={{
                display:"grid",
                gridTemplateColumns:"1fr 1fr",
                gap:"1.5rem",
                marginBottom:"2rem",
                background:"white",
                borderRadius:"16px",
                overflow:"hidden",
                boxShadow:"0 6px 18px rgba(0,0,0,0.08)"
              }}
            >
              <div
                style={{
                  minHeight:260,
                  backgroundImage:`url(${day.image})`,
                  backgroundSize:"cover",
                  backgroundPosition:"center"
                }}
              />

              <div style={{padding:"2rem"}}>

                <h3 style={{marginBottom:"1rem"}}>
                  Day {day.dayNum} — {day.title}
                </h3>

                <p style={{color:theme.textMuted, lineHeight:1.7}}>
                  {day.body}
                </p>

                <div style={{marginTop:"1rem"}}>
                  {day.tags.map((tag,idx)=>(
                    <span
                      key={idx}
                      style={{
                        marginRight:6,
                        background:"#ede9fe",
                        padding:"4px 10px",
                        borderRadius:20,
                        fontSize:"0.8rem"
                      }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div style={{marginTop:"1rem", fontSize:"0.9rem"}}>
                  {day.activities}
                </div>

              </div>
            </article>
          ))}
        </section>

        {/* CTA */}

        <section
          style={{
            textAlign:"center",
            marginTop:"4rem",
            padding:"3rem",
            background:"white",
            borderRadius:"20px",
            boxShadow:"0 6px 20px rgba(0,0,0,0.06)"
          }}
        >
          <h3 style={{marginBottom:"1rem"}}>
            Ready to Climb Kilimanjaro?
          </h3>

          <button
            style={{
              background:"linear-gradient(135deg,#7c3aed,#8b5cf6)",
              color:"white",
              border:"none",
              padding:"12px 28px",
              borderRadius:30,
              fontWeight:600,
              display:"inline-flex",
              alignItems:"center",
              gap:6
            }}
            onClick={()=>appContext.navTo({ item: 'plan-trip' })}
          >
            Book This Trek <IoMdArrowDropright/>
          </button>
        </section>

      </div>
    </div>
  );
}
